import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { SiteConfigManager, useApplicationState, useSettings } from '@atlas/core';
import { clientProfileRoutes } from '@atlas/routing';
import { Button, Label } from '@atlas/ui';

import { loadScript } from 'common/utils';
import background from 'static/assets/svg/achievements-bg.svg';

import { providers } from './constants';

export const SmarticoContext = React.createContext();

const loadSmarticoScript = async scriptPath => {
  if (window.SMARTICO_SCRIPT_IS_LOADED) return;

  if (window.SMARTICO_SCRIPT_IS_LOADING) {
    return new Promise(resolve => {
      window.SMARTICO_LOADING_CALLBACKS = window.SMARTICO_LOADING_CALLBACKS || [];
      window.SMARTICO_LOADING_CALLBACKS.push(resolve);
    });
  }

  window.SMARTICO_SCRIPT_IS_LOADING = true;
  await loadScript(scriptPath).then(() => {
    if (window.SMARTICO_LOADING_CALLBACKS?.length) {
      for (const cb of window.SMARTICO_LOADING_CALLBACKS) {
        cb();
      }
    }
  });

  window.SMARTICO_SCRIPT_IS_LOADED = true;
  window.SMARTICO_SCRIPT_IS_LOADING = false;
};

const isProduction = process.env.NODE_ENV === 'production';

const emptyKeys = {};

const emptyBalances = { diamonds: 0, gems: 0, points: 0 };

export const SmarticoProvider = ({ children }) => {
  const { goBack } = useHistory();
  const isSmartico =
    SiteConfigManager.getConfig('config.config.general.gamificationTool') === providers.SMARTICO;
  const smarticoConfig =
    SiteConfigManager.getConfig('config.config.gamification.smartico') || emptyKeys;
  const { account, authenticated } = useApplicationState();
  const { lang } = useSettings();
  const [ready, setReady] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [balances, setBalances] = useState(emptyBalances);
  const [smarticoLanguage, setSmarticoLanguage] = useState(null);
  const [scriptFailed, setScriptFailed] = useState(false);

  const openSmartico = useCallback(() => {
    if (!ready) return;
    // this opens smartico popup
    window._smartico.dp('dp:gf');
  }, [ready]);

  const openInbox = useCallback(() => {
    if (!ready) return;
    // this opens smartico inbox
    window._smartico.dp('dp:inbox');
  }, [ready]);

  // this is used to get unread messages count
  useEffect(() => {
    if (!window._smartico) return;

    const handlePropsChange = smarticoProps => {
      if (smarticoProps.core_inbox_unread_count !== undefined) {
        setUnreadMessagesCount(smarticoProps.core_inbox_unread_count);
      }
    };

    const handleCloseWidget = () => {
      if (window.location.search.includes(`cmd=${clientProfileRoutes.achievements}`)) {
        goBack();
      }
    };

    if (ready) {
      window._smartico.on('gf_closing', handleCloseWidget);

      if (smarticoConfig.enableInbox) {
        if (typeof window._smartico.getPublicProps === 'function') {
          const smarticoProps = window._smartico.getPublicProps();

          if (smarticoProps?.core_inbox_unread_count !== undefined) {
            setUnreadMessagesCount(smarticoProps.core_inbox_unread_count);
          }
        }

        window._smartico.on('props_change', handlePropsChange);
      }
    } else {
      window._smartico.off('props_change', handlePropsChange);
      window._smartico.off('gf_closing', handleCloseWidget);
    }

    return () => {
      if (window._smartico) {
        window._smartico.off('props_change', handlePropsChange);
        window._smartico.off('gf_closing', handleCloseWidget);
      }
    };
  }, [goBack, ready, smarticoConfig.enableInbox]);

  useEffect(() => {
    if (
      isSmartico &&
      smarticoConfig.brandKey &&
      smarticoConfig.labelKey &&
      smarticoConfig.scriptPath
    ) {
      if (authenticated) {
        const setUserData = () => {
          window._smartico_allow_localhost = !isProduction;
          window._smartico_user_id = account.id || null;
          // TODO: use 2 symbol language from useLanguage hook when it will be done
          window._smartico_language = account.id ? lang : null;
        };

        if (window.SMARTICO_SCRIPT_IS_LOADED) {
          setUserData();
          setReady(true);
        } else {
          loadSmarticoScript(smarticoConfig.scriptPath)
            .then(() => {
              window._smartico.on('init', () => setUserData());
              window._smartico.on('identify', () => setReady(true));

              window._smartico.init(smarticoConfig.labelKey, {
                brand_key: smarticoConfig.brandKey,
                debug: !isProduction,
              });
            })
            .catch(() => setScriptFailed(true));
        }
      } else {
        window._smartico_user_id = null;
        window._smartico_language = null;
        setReady(false);
      }
    }

    return () => {
      window._smartico_user_id = null;
      window._smartico_language = null;
    };
  }, [authenticated, account.id, isSmartico, smarticoConfig, lang]);

  // Keep the user's gamification balances (points / gems / diamonds) live from
  // the Smartico public props, independent of the inbox feature. Used by the
  // Store to drive affordability of the Buy button.
  useEffect(() => {
    if (!ready || !window._smartico) return undefined;

    // `props_change` carries only the props that changed, so a balance missing
    // from the payload means "unchanged", not zero.
    const readProps = smarticoProps => {
      if (!smarticoProps) return;
      setBalances(prev => ({
        diamonds: smarticoProps.ach_diamonds_balance ?? prev.diamonds,
        gems: smarticoProps.ach_gems_balance ?? prev.gems,
        points: smarticoProps.ach_points_balance ?? prev.points,
      }));
      if (smarticoProps.core_user_language) {
        setSmarticoLanguage(smarticoProps.core_user_language);
      }
    };

    if (typeof window._smartico.getPublicProps === 'function') {
      readProps(window._smartico.getPublicProps());
    }

    const handlePropsChange = () =>
      readProps(
        typeof window._smartico?.getPublicProps === 'function'
          ? window._smartico.getPublicProps()
          : null,
      );
    window._smartico.on('props_change', handlePropsChange);

    return () => {
      window._smartico?.off?.('props_change', handlePropsChange);
    };
  }, [ready]);

  const isSmarticoConfigInvalid =
    isSmartico &&
    (!smarticoConfig.brandKey || !smarticoConfig.labelKey || !smarticoConfig.scriptPath);

  const providedValues = {
    balances,
    hasSmarticoError: scriptFailed || isSmarticoConfigInvalid,
    isReadySmartico: ready,
    openInbox,
    openSmartico,
    smarticoLanguage,
    unreadMessagesCount,
  };

  return <SmarticoContext.Provider value={providedValues}>{children}</SmarticoContext.Provider>;
};

export function useSmartico() {
  return React.useContext(SmarticoContext) || {};
}

export const getSmarticoMissions = async ({ cb = () => {}, missionsUpdates = () => {} }) => {
  if (!window._smartico?.api) return;
  const missionsResponse = await window._smartico.api.getMissions({
    onUpdate: missionsUpdates,
  });

  return cb(missionsResponse);
};

export const requestMissionClaimReward = async ({ achCompletedId, missionId }) => {
  if (!window._smartico?.api) return;

  const response = await window._smartico.api.requestMissionClaimReward(missionId, achCompletedId);

  return response;
};

export const startMission = async ({ missionId }) => {
  if (!window._smartico?.api) return;

  const response = await window._smartico.api.requestMissionOptIn(missionId);

  return response;
};

export const getSmarticoTournaments = async ({ cb = () => {}, tournamentsUpdates = () => {} }) => {
  if (!window._smartico?.api) return;
  const tournamentsResponse = await window._smartico.api.getTournamentsList({
    onUpdate: tournamentsUpdates,
  });

  return cb(tournamentsResponse);
};

export const getSmarticoTournamentCb = async ({
  cb = () => {},
  tournamentId = '',
  tournaments,
}) => {
  if (tournamentId) {
    const tournament = tournaments.find(tournament => tournament.instance_id === +tournamentId);
    if (tournament) {
      const fullTournamentData = await window._smartico.api.getTournamentInstanceInfo(tournamentId);
      cb({ ...tournament, ...fullTournamentData });
    } else {
      cb(null);
    }
  }
};

export const getSmarticoTournament = async ({ cb = () => {}, tournamentId }) => {
  await getSmarticoTournaments({
    cb: tournaments =>
      getSmarticoTournamentCb({
        cb,
        tournamentId,
        tournaments,
      }),
    tournamentsUpdates: tournaments =>
      getSmarticoTournamentCb({
        cb,
        tournamentId,
        tournaments,
      }),
  });
};

export const registerSmarticoTournament = async ({ cb = () => {}, tournamentId }) => {
  if (!window._smartico?.api || !tournamentId) return;
  const tournamentsResponse = await window._smartico.api.registerInTournament(tournamentId);
  return cb(tournamentsResponse);
};

const Smartico = ({ onlyModal = false } = {}) => {
  const { openSmartico } = useSmartico();

  useEffect(() => {
    openSmartico();
  }, [openSmartico]);

  if (onlyModal) return null;

  return (
    <div className="achievements-content-wrapper achievements-empty">
      <img alt="" className="achievements-empty-background" src={background} />
      <div className="achievements-popup">
        <Label className="achievements-popup-message" message="achievements_empty_title" />
        <Button
          className="primary block"
          label="achievements_empty_button"
          onClick={openSmartico}
        />
      </div>
    </div>
  );
};

export default Smartico;
